import { Injectable } from "@angular/core";
import imageCompression, { Options } from "browser-image-compression";
import { LoggerService } from "./logger.service";

export interface FileWithIdentifier {
  file: File;
  identifier: string;
}

@Injectable({
  providedIn: "root"
})
export class CompressionService {

  constructor(private logger: LoggerService) { }

  async compressImage(fileWithIdentifier: FileWithIdentifier): Promise<FileWithIdentifier> {
    const baseOptions: Omit<Options, "useWebWorker"> = {
      maxSizeMB: 0.3,                // Target size (in megabytes)
      maxWidthOrHeight: 3840,        // target 4k resolution max dimension
      // Absolute URL (not a bare relative path) so importScripts() resolves correctly from the worker's blob: context on all devices
      libURL: new URL("assets/scripts/browser-image-compression.js", document.baseURI).href,
      onProgress: progress => {
        this.logger.info(`${fileWithIdentifier.file.name} Compression progress: ${progress}%`);
      },
      fileType: "image/jpeg"         // Or 'image/webp' if preferred
    };

    try {
      return await this.runCompression(fileWithIdentifier, { ...baseOptions, useWebWorker: true });
    } catch (err) {
      this.logger.error("Compression error (web worker):", err);
      try {
        // Worker creation/importScripts can fail in some mobile WebViews - retry on the main thread before giving up
        this.logger.infoWithTrace("Retrying compression on the main thread due to web worker failure.");
        return await this.runCompression(fileWithIdentifier, { ...baseOptions, useWebWorker: false });
      } catch (fallbackErr) {
        this.logger.error("Compression error (main-thread fallback):", fallbackErr);
        throw fallbackErr;
      }
    }
  }

  private async runCompression(fileWithIdentifier: FileWithIdentifier, options: Options): Promise<FileWithIdentifier> {
    const compressedFile = await imageCompression(fileWithIdentifier.file, options);
    return {
      file: compressedFile,
      identifier: fileWithIdentifier.identifier
    };
  }
}
